import { useState } from 'react'
import { useSelector } from 'react-redux'
import styled, { useTheme } from 'styled-components'
import { ProductOptionForCartScheme } from '@/apis/rest/generated/types'
import { StyledDivider } from '@/components/common/divider'
import FlatIcon from '@/components/common/icons/FlatIcon'
import { InputCheckbox } from '@/components/common/inputs'
import ToolTip from '@/components/common/toolTips/ToolTip'
import { Typo, TypoVariant } from '@/components/common/typography'
import { CartCounter } from '@/components/domains/cart/CartCount'
import { CartItemDeleteModal } from '@/components/domains/cart/CartItemDeleteModal'
import { GlobalSizeEnum } from '@/constants/global-size.enum'
import { IconNameEnum } from '@/constants/icon-name.enum'
import { S3_HOST_URL } from '@/constants/legacy/constEnv'
import ROUTES from '@/constants/legacy/constRoutes'
import { TooltipArrowPositionEnum } from '@/constants/tooltip-arrow-position.enum'
import { TooltipTypeEnum } from '@/constants/tooltip-type-enum'
import { useCartController } from '@/containers/hooks/cart/useCartController'
import { useDebounceCallback } from '@/containers/hooks/common/useDebounceCallback.hook'
import useCustomRouter from '@/containers/hooks/useCustomRouter'
import ArrowDownPrice from '@/images/arrow-down-price.svg'
import { RootState } from '@/stores/store'
import { encodeId } from '@/utils/utilApi'
import { currencyText } from '@/utils/utilNumber'

export const CartProductsV2 = () => {
  const [deleteTargetCartItemId, setDeleteTargetCartItemId] = useState<number | null>(null)
  const { push } = useCustomRouter()
  const { color, iconSize } = useTheme()
  const cartData = useSelector((state: RootState) => state.reduxDataReducers.cartData)
  const { updateCartCount, clickProductCouponButton, changeOption, deleteEachProduct, checkEachProduct } =
    useCartController()

  const handleClickEachDelete = (cartItemId: number) => {
    setDeleteTargetCartItemId(cartItemId)
  }

  const handleClickProductCouponButton = (cartProductOptionId: number) => {
    clickProductCouponButton(cartProductOptionId)
  }
  const handleClickChangeOption = (
    cartItemId: number,
    productOptionId: number,
    options: ProductOptionForCartScheme[]
  ) => {
    changeOption(cartItemId, productOptionId, options)
  }

  const handleUpdateCartCount = (data: {
    cartItemId: number
    cartProductOptionId: number
    count: number
    prevCount: number
  }) => {
    updateCartCount(data)
  }

  const handleCheckEachProduct = (cartProductOptionId: number) => {
    checkEachProduct(cartProductOptionId)
  }
  const { debouncedFunction } = useDebounceCallback({
    callback: handleUpdateCartCount,
    delay: 100,
  })

  const handleClickThumbnail = ({ productId, promotionId }: { productId: number; promotionId?: number }) => {
    push({
      pathname: ROUTES.PRODUCTS.DETAIL,
      query: {
        productId,
        ...(promotionId && { promotion: encodeId('ProductPromotionType', promotionId) }),
      },
    })
  }

  const handleCloseDeleteModal = () => {
    setDeleteTargetCartItemId(null)
  }

  return (
    <>
      {cartData?.shippings.map((cartShippingGroup, index) => {
        const { amount, productAmount, isFreeShipping, shortfallProductAmountForFreeShipping } = cartShippingGroup
        return (
          <StyledCartGroup key={`${cartShippingGroup.sellerId}-${index}`} className="cart-group">
            <div className="cart-group__header">
              <div className="cart-group__title">
                <Typo variant={TypoVariant.Body1Bold}>{cartShippingGroup.sellerShopName}</Typo>
              </div>
              {index !== 0 && (
                <div className="cart-group__shipping-info">
                  <FlatIcon type={IconNameEnum.IcCaution} size={iconSize.size16} color={color.gray400} />
                  <Typo variant={TypoVariant.Body3Regular} color={color.gray400}>
                    출고지가 다른 분리 배송 상품입니다.
                  </Typo>
                </div>
              )}
            </div>

            {cartShippingGroup?.orderItems.map(
              (
                {
                  availableQuantity,
                  productId,
                  productOptionId,
                  isChecked,
                  productMainImagePath,
                  productOptionName,
                  productName,
                  isApproved,
                  hasAvailableProductCoupon,
                  amount,
                  quantity,
                  appliedCouponId,
                  originalAmount,
                  productOptions,
                  cartItemId,
                  isSingleOption,
                  productPromotionId,
                  onSale,
                  isRealSoldOut,
                  isDeletedProductOption,
                  isPriceDown,
                },
                orderItemIndex
              ) => {
                const isDisable = availableQuantity === 0 || !isApproved || !onSale || isRealSoldOut
                const isOtherOptionSoldOut = productOptions
                  .filter((option) => option.id !== productOptionId)
                  .every((option) => option.availableQuantity === 0)

                // 전체 옵션 품절
                const isAllOptionSoldOut = productOptions.every((option) => option.availableQuantity === 0)

                // 현재 옵션만 품절
                const isOnlyCurrentOptionSoldOut = availableQuantity === 0 && !isOtherOptionSoldOut

                // 수동 품절
                const isManualSoldOut = availableQuantity > 0 && !onSale

                return (
                  <StyledCartOption key={productOptionId} isDisable={isDisable} className="cart-group__option">
                    <div className="cart-group-option__product">
                      <InputCheckbox
                        id={`check-${productOptionId}`}
                        onChange={() => handleCheckEachProduct(productOptionId)}
                        checked={isChecked}
                        size={GlobalSizeEnum.Lg}
                        disabled={isDisable}
                      />
                      <div className="cart-group-option__product-info">
                        <div
                          className="cart-group-option__product-image"
                          onClick={() => handleClickThumbnail({ productId, promotionId: productPromotionId })}
                        >
                          <img alt={productMainImagePath} src={`${S3_HOST_URL}/${productMainImagePath}`} />
                          {isDisable && (
                            <div className="cart-group-option__product-image-overlay">
                              <Typo variant={TypoVariant.Body4SemiBold} color={color.grayWhite}>
                                {isDeletedProductOption ? '구매 불가' : !isApproved ? '상품 준비중' : '입고 예정'}
                              </Typo>
                            </div>
                          )}
                        </div>
                        <div className="cart-group-option__product-group">
                          <div className="cart-group-option__product-top">
                            <div className="cart-group-option__product-title">
                              <Typo
                                variant={TypoVariant.Body3Regular}
                                style={{
                                  display: '-webkit-box',
                                  WebkitBoxOrient: 'vertical',
                                  WebkitLineClamp: 2,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  wordBreak: 'break-all',
                                }}
                                color={isDisable ? color.gray400 : color.gray900}
                              >
                                {productName}
                              </Typo>
                            </div>
                            <div
                              className="cart-group-option__product-close"
                              onClick={() => handleClickEachDelete(cartItemId)}
                            >
                              <FlatIcon type={IconNameEnum.IcClose} size={iconSize.size20} color={color.gray300} />
                            </div>
                          </div>
                          <div className="cart-group-option__product-bottom">
                            <div className="cart-group-option__product-bottom-coupon-price">
                              {!!appliedCouponId && (
                                <Typo variant={TypoVariant.Caption1Bold} color={color.red500}>
                                  쿠폰적용됨
                                </Typo>
                              )}
                              <Typo variant={TypoVariant.Body1Bold} color={isDisable ? color.gray400 : color.gray900}>
                                {currencyText(amount)}
                              </Typo>
                              {originalAmount !== amount && (
                                <Typo
                                  variant={TypoVariant.Body4Regular}
                                  color={color.gray300}
                                  style={{
                                    textDecoration: 'line-through',
                                  }}
                                >
                                  {currencyText(originalAmount)}
                                </Typo>
                              )}
                            </div>
                            <div className="cart-group-option__product-bottom-button">
                              {hasAvailableProductCoupon && !isDisable && (
                                <StyledCouponButton
                                  isApply={!!appliedCouponId}
                                  onClick={() => handleClickProductCouponButton(productOptionId)}
                                >
                                  <Typo
                                    variant={TypoVariant.Body3Medium}
                                    color={!!appliedCouponId ? color.gray900 : color.grayWhite}
                                  >
                                    {!!appliedCouponId ? '쿠폰변경' : '쿠폰적용'}
                                  </Typo>
                                </StyledCouponButton>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* 가격 변동 넛징 영역*/}
                    {isPriceDown && (
                      <StyledPriceDownNudge>
                        <Typo variant={TypoVariant.Body3Bold} color={color.red500}>
                          담았을 때 가격보다 내려갔어요
                        </Typo>
                        <ArrowDownPrice />
                      </StyledPriceDownNudge>
                    )}

                    {/* 수량 변경 영역*/}
                    <div className="cart-group-option__detail">
                      <Typo
                        style={{
                          display: '-webkit-box',
                          WebkitBoxOrient: 'vertical',
                          WebkitLineClamp: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          wordBreak: 'break-all',
                        }}
                        variant={TypoVariant.Body3Regular}
                        color={isDisable ? color.gray400 : color.gray700}
                      >
                        {productOptionName}
                      </Typo>
                      <div className="cart-group-option__detail-bottom">
                        {((!isSingleOption && !isAllOptionSoldOut && onSale && isApproved && !isManualSoldOut) ||
                          isOnlyCurrentOptionSoldOut) && (
                          <div
                            className="cart-group-option__detail-bottom-option-change"
                            onClick={() => handleClickChangeOption(cartItemId, productOptionId, productOptions)}
                          >
                            <Typo variant={TypoVariant.Body4Bold} color={color.gray500}>
                              옵션변경
                            </Typo>
                          </div>
                        )}
                        <StyledCartCounterWrapper>
                          <CartCounter
                            isDisabled={isDisable}
                            value={quantity}
                            onChange={(value) =>
                              debouncedFunction({
                                cartItemId,
                                cartProductOptionId: productOptionId,
                                count: value,
                                prevCount: quantity,
                              })
                            }
                            isDisabledAdd={quantity >= (availableQuantity || 0)}
                            max={20}
                          />
                          {!isDisable && quantity > (availableQuantity || 0) && (
                            <div style={{ position: 'absolute' }}>
                              <ToolTip
                                alwaysVisible
                                tooltipStyle={{
                                  top: '10px',
                                  transform: 'translateX(-6%)',
                                  padding: '4px 8px',
                                  minWidth: '125px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                                tooltipType={TooltipTypeEnum.Blue}
                                tooltipDataStyle={{
                                  color: color.blue500,
                                  variant: TypoVariant.Body5Medium,
                                }}
                                tooltipData={`구매가능수량 : ${availableQuantity}개`}
                                arrowPosition={TooltipArrowPositionEnum.TopMiddle}
                              />
                            </div>
                          )}
                        </StyledCartCounterWrapper>
                      </div>
                    </div>
                    {orderItemIndex !== cartShippingGroup.orderItems.length - 1 && (
                      <div className="cart-group-option__divider" />
                    )}
                  </StyledCartOption>
                )
              }
            )}

            {/* 금액 상세 */}
            <div className="cart-group__summary">
              <div className="cart-group__summary-item-total">
                <Typo variant={TypoVariant.Heading5}>{currencyText(productAmount)}</Typo>
              </div>
            </div>
            <div className="cart-group__dvider" />
            <div className="cart-group__summary-shipping">
              <Typo variant={TypoVariant.Body3Medium} color={color.gray700}>
                배송비 {currencyText(amount)}
              </Typo>
              {!!shortfallProductAmountForFreeShipping && !isFreeShipping && (
                <Typo variant={TypoVariant.Body5Regular} color={color.gray400}>
                  묶음배송 상품 {currencyText(shortfallProductAmountForFreeShipping)} 추가 시 무료배송
                </Typo>
              )}
            </div>
            <StyledDivider height="0.8rem" backgroundColor={color.gray50} />
          </StyledCartGroup>
        )
      })}
      {!!deleteTargetCartItemId && (
        <CartItemDeleteModal
          deleteTargetId={deleteTargetCartItemId}
          onConfirm={deleteEachProduct}
          isShow={!!deleteTargetCartItemId}
          onClose={handleCloseDeleteModal}
        />
      )}
    </>
  )
}

const StyledCartGroup = styled.div`
  .cart-group {
    &__header {
      margin-bottom: 2rem;
    }

    &__title {
      padding: 1.6rem;
      border-bottom: ${({
        theme: {
          color: { gray70 },
        },
      }) => `0.1rem solid ${gray70}`};
    }

    &__shipping-info {
      margin-top: 0.8rem;
      padding: 0 1.6rem;
      display: flex;
      align-items: center;
      gap: 0.4rem;
    }

    &__dvider {
      height: 0.1rem;
      background-color: ${({
        theme: {
          color: { gray70 },
        },
      }) => gray70};
    }

    &__summary {
      margin-top: 2rem;
      padding: 0 1.6rem 1.6rem;
      display: flex;
      flex-direction: column;
      gap: 0.6rem;
      &-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 1.2rem;
      }
      &-item-inner {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 1.2rem;
        margin-left: 0.8rem;
      }

      &-item-shipping {
        display: flex;
        justify-content: space-between;
        align-items: start;
      }

      &-item-shipping-price {
        display: flex;
        flex-direction: column;
        align-items: end;
        justify-content: start;
      }
      &-item-total {
        display: flex;
        justify-content: flex-end;
      }
      &-shipping {
        padding: 0.8rem 1.6rem 1.6rem;
        display: flex;
        flex-direction: column;
        align-items: center;
      }
    }
  }
`

const StyledCartOption = styled.div<{ isDisable: boolean }>`
  padding: 0 1.6rem;
  display: flex;
  flex-direction: column;

  .cart-group-option {
    &__divider {
      height: 0.1rem;
      margin: 2rem 0;
      background-color: ${({
        theme: {
          color: { gray50 },
        },
      }) => gray50};
    }
    &__product {
      display: flex;
      align-items: start;
      gap: 1.2rem;
      margin-bottom: 0.6rem;

      &-info {
        display: flex;
        align-items: start;
        flex: 1;
        width: 100%;
      }

      &-image {
        border: ${({
          theme: {
            color: { gray50 },
          },
        }) => `solid 0.1rem ${gray50}`};
        width: 7.6rem;
        height: 7.6rem;
        margin-right: 1.2rem;
        position: relative;
        border-radius: 6px;
        overflow: hidden;
        flex-shrink: 0;
        cursor: pointer;
        img {
          width: 100%;
          height: 100%;
          aspect-ratio: 1;
        }
      }

      &-image-overlay {
        position: absolute;
        bottom: 0;
        right: 0;
        left: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 0.3rem 0 0.4rem;
        background-color: rgba(0, 0, 0, 0.5);
      }

      &-group {
        display: flex;
        flex-direction: column;
        gap: 0.8rem;
        width: 100%;
      }

      &-top {
        display: flex;
        justify-content: space-between;
        align-items: start;
        gap: 2rem;
        flex: 1;
      }

      &-title {
        width: 100%;
        flex: 1;
      }

      &-close {
        cursor: pointer;
        padding-bottom: 1.2rem;
      }

      &-bottom {
        display: flex;
        justify-content: space-between;
        align-items: start;
        gap: 1.2rem;
      }

      &-bottom-coupon-price {
        display: flex;
        flex-direction: column;
      }

      &-bottom-price-group {
        display: flex;
        gap: 0.25rem;
      }

      &-bottom-button {
        display: flex;
        align-items: center;
      }
    }

    &__detail {
      border-radius: 0.8rem;
      background-color: ${({
        theme: {
          color: { gray50 },
        },
      }) => gray50};
      padding: 1.6rem;
      display: flex;
      flex-direction: column;
      gap: 0.8rem;
      &-bottom {
        display: flex;
        align-items: end;
        gap: 1.2rem;
        justify-content: space-between;

        &-option-change {
          cursor: pointer;
        }
      }
    }
  }
  color: ${({ isDisable, theme }) => isDisable && theme.color.gray300};
`

const StyledCouponButton = styled.button<{ isApply: boolean }>`
  padding: 0.6rem 1.4rem;
  background-color: ${({
    isApply,
    theme: {
      color: { red500, grayWhite },
    },
  }) => (isApply ? grayWhite : red500)};
  color: ${({
    isApply,
    theme: {
      color: { grayWhite, gray900 },
    },
  }) => (isApply ? gray900 : grayWhite)};
  border: none;
  border-radius: 8px;
  border: ${({ isApply, theme }) => (isApply ? `1px solid ${theme.color.gray200}` : 'none')};
  cursor: pointer;
`

const StyledCartCounterWrapper = styled.div`
  position: relative;
  margin-left: auto;
`

const StyledPriceDownNudge = styled.div`
  background-color: ${({ theme }) => theme.color.red30};
  padding: 1rem 0.6rem;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 0.8rem;
  margin-bottom: 1.2rem;
  gap: 0.2rem;
`
